/**
 * Xarita — MapLibre GL (WebGL) asosida.
 *
 * Katta qatlamlar (millionlab bino/yo'l) server vektor plitkalari bilan
 * ko'rsatiladi (`vectorLayers`): brauzer faqat ekrandagi plitkalarni oladi,
 * geometriya GPU'da chiziladi — shu sababli xarita qotmaydi. Kichik, tayyor
 * ma'lumotlar (`points`, `shapes`) esa GeoJSON manbasi bilan chiziladi.
 *
 * MapLibre og'ir kutubxona (~230 KB gzip), shuning uchun u faqat xarita
 * ochilganda alohida modul sifatida yuklanadi — bosh sahifa va boshqa
 * sahifalar vazni oshmaydi. Tashqi serverga (CDN) so'rov yo'q: kod va CSS
 * o'z domenimizdan, ishchi oqim (`worker`) esa `blob:` orqali (CSP ruxsat bergan).
 *
 * Boshqaruv: sichqoncha/barmoq bilan surish va kattalashtirish, `+`/`−`
 * tugmalari, klaviatura (strelkalar, `+`/`−`), «Ma'lumot bor hududga
 * yaqinlashish» tugmasi (silliq «uchish»), podkladka tanlagich, to'liq ekran,
 * poligon chizish rejimi, obyekt bosilganda popup.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  GeoJSONSource,
  Map as GlMap,
  MapMouseEvent,
  Marker as GlMarker,
  RasterTileSource,
  VectorTileSource,
} from "maplibre-gl";

import { BASE_URL } from "@/shared/api/client";
import { authApi, gisApi } from "@/shared/api/endpoints";
import type {
  MapBasemap,
  MapBounds,
  MapFocus,
  MapInsets,
  MapPin,
  MapPoint,
  MapShape,
  MapViewport,
  VectorLayer,
} from "@/shared/lib/mapGeo";

export * from "@/shared/lib/mapGeo";

const MIN_ZOOM = 3;
const MAX_ZOOM = 18;
/** Server shu masshtabgacha plitka beradi; undan yuqorida mijoz kattalashtirib ko'rsatadi. */
const TILE_SOURCE_MAX_ZOOM = 15;
/** «Ma'lumotga yaqinlashish»da eng katta masshtab — kichik obyekt butun ekranni egallab olmasin. */
const FIT_MAX_ZOOM = 15;
const FIT_PADDING = 32;
/** O'ngdagi asboblar ustuni (`.map-tools`): 34 px tugma + 12 px chekka. */
const TOOLS_WIDTH = 46;
const FLIGHT_MS = 1400;

/* Default massivlar modul darajasida: parametrdagi `= []` har renderda yangi
   massiv yaratadi va unga bog'liq effektlarni cheksiz qayta ishga tushiradi. */
const NO_POINTS: MapPoint[] = [];
const NO_SHAPES: MapShape[] = [];
const NO_PINS: MapPin[] = [];
const NO_VECTORS: VectorLayer[] = [];
const NO_INSETS: MapInsets = {};

type MapLibre = typeof import("maplibre-gl");

let libraryPromise: Promise<MapLibre> | null = null;

/** MapLibre va uning CSS'i bir marta, xarita birinchi marta ochilganda yuklanadi. */
function loadMapLibrary(): Promise<MapLibre> {
  libraryPromise ??= Promise.all([
    import("maplibre-gl"),
    import("maplibre-gl/dist/maplibre-gl.css"),
  ]).then(([module]) => (module as unknown as { default?: MapLibre }).default ?? (module as unknown as MapLibre));
  return libraryPromise;
}

/**
 * CSS o'zgaruvchisini haqiqiy rangga aylantiradi: WebGL `var(--brand)` kabi
 * qiymatni tushunmaydi.
 */
function resolveColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const match = /^var\(\s*(--[\w-]+)\s*\)$/.exec(value.trim());
  if (!match) return value;
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim();
  return resolved || fallback;
}

function prefersReducedMotion(): boolean {
  return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

function tileUrl(layer: VectorLayer): string {
  const base = BASE_URL.startsWith("http") ? BASE_URL : `${window.location.origin}${BASE_URL}`;
  return `${base}/gis/tiles/${layer.layerId}/{z}/{x}/{y}.pbf?v=${layer.version}`;
}

/* --------------------------------------------------------- GeoJSON qurish */

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

function dashKey(dash: number[] | undefined): string {
  return dash && dash.length > 0 ? dash.join(",") : "";
}

function shapesToGeoJson(shapes: MapShape[]): GeoJSON.FeatureCollection {
  const fallback = resolveColor("var(--brand)", "#16a34a");
  const features: GeoJSON.Feature[] = [];
  for (const shape of shapes) {
    const color = resolveColor(shape.color, fallback);
    const properties = {
      id: shape.id,
      color,
      stroke: resolveColor(shape.strokeColor, color),
      fillOpacity: shape.fillOpacity ?? 0.25,
      strokeWidth: shape.strokeWidth ?? (shape.line ? 1.6 : 1),
      dash: dashKey(shape.dash),
    };
    if (shape.line) {
      const lines = shape.rings.filter((ring) => ring.length >= 2);
      if (lines.length === 0) continue;
      features.push({
        type: "Feature",
        id: undefined,
        properties,
        geometry:
          lines.length === 1
            ? { type: "LineString", coordinates: lines[0] }
            : { type: "MultiLineString", coordinates: lines },
      });
      continue;
    }
    /* Ko'pburchakning tuzilishi (tashqi halqa + teshiklar) saqlangan bo'lsa shundan,
       aks holda har bir halqa alohida ko'pburchak. */
    const polygons = (shape.polygons ?? shape.rings.map((ring) => [ring])).filter(
      (polygon) => polygon[0] && polygon[0].length >= 3,
    );
    if (polygons.length === 0) continue;
    features.push({
      type: "Feature",
      properties,
      geometry:
        polygons.length === 1
          ? { type: "Polygon", coordinates: polygons[0] }
          : { type: "MultiPolygon", coordinates: polygons },
    });
  }
  return { type: "FeatureCollection", features };
}

function pointsToGeoJson(points: MapPoint[], fallback: string): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: points.map((point) => ({
      type: "Feature",
      properties: { id: point.id, color: resolveColor(point.color, fallback) },
      geometry: { type: "Point", coordinates: [point.lon, point.lat] },
    })),
  };
}

/** Plitka obyektidagi sarlavha: `titleKeys` bo'yicha birinchi to'lgan atribut, bo'lmasa qatlam nomi. */
function featureTitle(properties: Record<string, unknown>, layer: VectorLayer): string {
  for (const key of ["name", ...(layer.titleKeys ?? [])]) {
    const value = properties[key];
    if (value !== null && value !== undefined && value !== "") return String(value);
  }
  return layer.label;
}

/* ---------------------------------------------------------------- stillar */

type LayerSpec = import("maplibre-gl").LayerSpecification;

/** `["get","color"]` bo'lsa shu rang (CSS satri), aks holda qatlam rangi. */
function colorExpression(fallback: string): import("maplibre-gl").ExpressionSpecification {
  return [
    "case",
    ["==", ["typeof", ["get", "color"]], "string"],
    ["to-color", ["get", "color"]],
    fallback,
  ];
}

function vectorLayerSpecs(layer: VectorLayer): LayerSpec[] {
  const source = `vt:${layer.id}`;
  const color = resolveColor(layer.color, "#16a34a");
  const stroke = resolveColor(layer.strokeColor, color);
  const minzoom = Math.max(0, layer.minZoom ?? 0);
  const maxzoom = layer.maxZoom && layer.maxZoom < 22 ? layer.maxZoom : undefined;
  const common = { source, "source-layer": "f", minzoom, ...(maxzoom ? { maxzoom } : {}) } as const;
  const selectedWidth = (base: number): import("maplibre-gl").ExpressionSpecification => [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    base + 2.5,
    base,
  ];

  /* Umumlashtirilgan qamrov (zich qatlam): past masshtabda ma'lumot bor hududlar yaxlit poligon,
     yaqinlashgan sari bo'laklarga ajraladi; aniq obyektlar uning ustida chiqadi. */
  const density: LayerSpec[] = layer.densityUntil
    ? [
        {
          id: `${source}:density`,
          type: "fill",
          source,
          "source-layer": "d",
          maxzoom: layer.densityUntil,
          paint: { "fill-color": color, "fill-opacity": 0.28 },
        },
        {
          id: `${source}:density:edge`,
          type: "line",
          source,
          "source-layer": "d",
          maxzoom: layer.densityUntil,
          layout: { "line-join": "round" },
          paint: { "line-color": color, "line-width": 1.5, "line-opacity": 0.95 },
        },
      ]
    : [];
  /* Piksel ostidagi kichik obyektlar server tomonidan nuqtaga aylantirilgan — ko'rinib turadi. */
  const dots = (): LayerSpec => ({
    id: `${source}:dots`,
    type: "circle",
    ...common,
    filter: ["==", ["geometry-type"], "Point"],
    paint: {
      "circle-color": colorExpression(color),
      "circle-radius": 2.6,
      "circle-opacity": 0.9,
    },
  });

  if (layer.geometry === "polygon") {
    return [
      ...density,
      {
        id: `${source}:fill`,
        type: "fill",
        ...common,
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: {
          "fill-color": colorExpression(color),
          "fill-opacity": layer.fillOpacity ?? 0.25,
        },
      },
      {
        id: `${source}:line`,
        type: "line",
        ...common,
        layout: { "line-join": "round" },
        paint: {
          "line-color": layer.strokeColor ? stroke : colorExpression(color),
          "line-width": selectedWidth(layer.strokeWidth ?? 1),
          ...(layer.dash && layer.dash.length > 0 ? { "line-dasharray": layer.dash } : {}),
        },
      },
      dots(),
    ];
  }
  if (layer.geometry === "line") {
    return [
      ...density,
      {
        id: `${source}:line`,
        type: "line",
        ...common,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": layer.strokeColor ? stroke : colorExpression(color),
          "line-width": selectedWidth(layer.strokeWidth ?? 1.6),
          ...(layer.dash && layer.dash.length > 0 ? { "line-dasharray": layer.dash } : {}),
        },
      },
      dots(),
    ];
  }
  return [
    ...density,
    {
      id: `${source}:point`,
      type: "circle",
      ...common,
      paint: {
        "circle-color": colorExpression(color),
        "circle-radius": ["case", ["boolean", ["feature-state", "selected"], false], 9, 6],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    },
  ];
}

/* ------------------------------------------------------------------ popup */

type PopupContent =
  | { kind: "point"; point: MapPoint; lng: number; lat: number }
  | { kind: "shape"; shape: MapShape; lng: number; lat: number }
  | {
      kind: "vector";
      layer: VectorLayer;
      featureId: string;
      title: string;
      properties: Record<string, unknown>;
      lng: number;
      lat: number;
    };

/** Popup'dagi meta atributlarda ko'rsatilmaydigan texnik maydonlar. */
const HIDDEN_PROPERTY_KEYS = new Set(["color", "layer", "name"]);

function formatPropertyLabel(key: string): string {
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatPropertyValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  }
  if (typeof value === "boolean") return value ? "Ha" : "Yo'q";
  return String(value);
}

/** Popup ichida bosilgan obyektning barcha atributlarini (metadata) ro'yxat qilib chiqaradi. */
function PropertyList({ properties }: { properties?: Record<string, unknown> }) {
  const entries = Object.entries(properties ?? {}).filter(
    ([key, value]) =>
      !HIDDEN_PROPERTY_KEYS.has(key) && value !== null && value !== undefined && value !== "",
  );
  if (entries.length === 0) return null;
  return (
    <dl className="map-popup-meta">
      {entries.map(([key, value]) => (
        <div key={key} className="map-popup-meta-row">
          <dt>{formatPropertyLabel(key)}</dt>
          <dd>{formatPropertyValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function pinElement(pin: MapPin): HTMLElement {
  const element = document.createElement("span");
  element.className = `map-pin is-${pin.tone ?? "brand"} is-gl`;
  element.setAttribute("role", "img");
  element.setAttribute("aria-label", pin.label);
  element.title = pin.label;
  element.innerHTML =
    '<svg width="28" height="36" viewBox="0 0 28 36" aria-hidden="true">' +
    '<path d="M14 1C7 1 1.5 6.4 1.5 13.2 1.5 22.5 14 35 14 35s12.5-12.5 12.5-21.8C26.5 6.4 21 1 14 1z" />' +
    '<circle cx="14" cy="13" r="4.6" /></svg>';
  return element;
}

const ICON = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export function MapView({
  points = NO_POINTS,
  shapes = NO_SHAPES,
  vectorLayers = NO_VECTORS,
  basemaps,
  center = { lat: 41.31, lon: 69.24 },
  zoom: initialZoom = 6,
  onSelect,
  onViewportChange,
  popup = true,
  loading = false,
  fullscreenActive = false,
  onToggleFullscreen,
  onBack,
  drawMode = false,
  onDrawFinish,
  extraTools,
  focus,
  onMapClick,
  pin,
  onPinDrag,
  pins = NO_PINS,
  dataBounds,
  fitInsets = NO_INSETS,
  fitTarget,
  scaleBar = false,
}: {
  /** Tashqaridan boshqariladigan markaz (qidiruv natijasi, geolokatsiya). */
  focus?: MapFocus | null;
  /** Xaritaning bo'sh joyi bosilganda (surish emas) — nuqta koordinatasi. */
  onMapClick?: (point: { lat: number; lon: number }) => void;
  /** Bitta ajratilgan belgi (masalan tanlangan murojaat nuqtasi). */
  pin?: MapPin | null;
  /** Berilsa, `pin` belgisini sudrab ko'chirish mumkin — qo'yib yuborilgan joy koordinatasi. */
  onPinDrag?: (point: { lat: number; lon: number }) => void;
  /** Qo'shimcha belgilar (masalan foydalanuvchi joylashuvi). */
  pins?: MapPin[];
  /** Tayyor nuqtalar (kichik ma'lumot: hodisalar, qurilmalar). */
  points?: MapPoint[];
  /** Tayyor shakllar (kichik ma'lumot: ijara uchastkalari, oldindan ko'rish). */
  shapes?: MapShape[];
  /** Server vektor plitkalaridan chiziladigan qatlamlar (katta qatlamlar). Pastdagisi birinchi chiziladi. */
  vectorLayers?: VectorLayer[];
  /** Podkladkalar; birinchisi standart bo'lib ochiladi. */
  basemaps: MapBasemap[];
  center?: { lat: number; lon: number };
  zoom?: number;
  /** Nuqta bosilganda chaqiriladi; ikkinchi argument — nuqtaning maydondagi o'rni. */
  onSelect?: (id: string, place: { x: number; y: number; width: number; height: number }) => void;
  /** Ko'rinib turgan hudud (masshtab + burchaklar) o'zgarib bo'lganda chaqiriladi. */
  onViewportChange?: (viewport: MapViewport) => void;
  /** Obyekt bosilganda popup (tashqi kartochka bo'lsa o'chiriladi). */
  popup?: boolean;
  /** Tashqi ma'lumot hali kelmagan — yuklanish pardasi ko'rsatiladi. */
  loading?: boolean;
  /** To'liq ekran holati — `map-tools`dagi tugma ko'rinishini belgilaydi. */
  fullscreenActive?: boolean;
  /** Berilsa, `map-tools`da to'liq ekran almashtirgich tugmasi chiqadi. */
  onToggleFullscreen?: () => void;
  /** Berilsa, `map-tools`ning eng boshida orqaga (ro'yxatga) tugmasi chiqadi. */
  onBack?: () => void;
  /** Poligon chizish rejimi: bosilgan har bir nuqta qo'shiladi, ikki marta bosish yakunlaydi. */
  drawMode?: boolean;
  /** Chizish yakunlanganda chaqiriladi — yopiq halqa, `[lon, lat]` juftliklari. */
  onDrawFinish?: (ring: Array<[number, number]>) => void;
  /** `map-tools` eng tepasiga qo'shiladigan ixtiyoriy tugmalar. */
  extraTools?: React.ReactNode;
  /**
   * Ma'lumot joylashgan hudud. Berilsa (`null` ham), «Ma'lumot bor hududga
   * yaqinlashish» tugmasi chiqadi; `null` — ko'rsatiladigan ma'lumot yo'q (tugma o'chiq).
   */
  dataBounds?: MapBounds | null;
  /** Xarita ustidagi panellar egallagan chetlar — qarang: `MapInsets`. */
  fitInsets?: MapInsets;
  /** Tashqaridan chegara bo'yicha yaqinlashish (masalan ro'yxatda tanlangan obyekt). `key` har safar yangi. */
  fitTarget?: { bounds: MapBounds; key: string | number } | null;
  /** Masofa masshtabi (chap pastda). */
  scaleBar?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GlMap | null>(null);
  const libRef = useRef<MapLibre | null>(null);
  const [ready, setReady] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const [tilesBusy, setTilesBusy] = useState(false);
  const [basemapCode, setBasemapCode] = useState("");
  const [basemapOpen, setBasemapOpen] = useState(false);
  const [drawPoints, setDrawPoints] = useState<Array<{ lat: number; lon: number }>>([]);
  const [popupContent, setPopupContent] = useState<PopupContent | null>(null);
  const [popupAt, setPopupAt] = useState<{ x: number; y: number } | null>(null);

  /* Eng so'nggi qiymatlar: xarita hodisa tinglovchilari bir marta ulanadi. */
  const latest = useRef({ points, shapes, vectorLayers, popup, onSelect, onMapClick, onPinDrag, drawMode, onViewportChange });
  latest.current = { points, shapes, vectorLayers, popup, onSelect, onMapClick, onPinDrag, drawMode, onViewportChange };
  const onDrawFinishRef = useRef(onDrawFinish);
  onDrawFinishRef.current = onDrawFinish;
  const popupRef = useRef<PopupContent | null>(null);
  popupRef.current = popupContent;
  const drawRef = useRef(drawPoints);
  drawRef.current = drawPoints;
  const vectorState = useRef(new Map<string, { signature: string; layerIds: string[] }>());
  const dashGroups = useRef(new Set<string>());
  const selectedFeature = useRef<{ source: string; id: string } | null>(null);
  const infoRequest = useRef(0);

  const basemap = basemaps.find((row) => row.code === basemapCode) ?? basemaps[0];

  /* ------------------------------------------------------------ yaratish */
  useEffect(() => {
    let disposed = false;
    let map: GlMap | null = null;

    loadMapLibrary()
      .then((lib) => {
        if (disposed || !host.current) return;
        libRef.current = lib;
        try {
          map = new lib.Map({
            container: host.current,
            style: {
              version: 8,
              sources: {},
              layers: [
                {
                  id: "background",
                  type: "background",
                  paint: { "background-color": resolveColor("var(--surface-inset)", "#e5e7eb") },
                },
              ],
            },
            center: [center.lon, center.lat],
            zoom: initialZoom,
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
            attributionControl: false,
            dragRotate: false,
            pitchWithRotate: false,
            touchPitch: false,
            doubleClickZoom: true,
            fadeDuration: 150,
          });
        } catch {
          /* WebGL yo'q yoki o'chirilgan */
          setUnsupported(true);
          return;
        }
        map.touchZoomRotate.disableRotation();
        mapRef.current = map;
        const gl = map;

        gl.on("load", () => {
          if (disposed) return;
          const pointColor = resolveColor("var(--brand)", "#16a34a");
          gl.addSource("shapes", { type: "geojson", data: EMPTY, promoteId: "id" });
          gl.addSource("points", { type: "geojson", data: EMPTY, promoteId: "id" });
          gl.addSource("draw", { type: "geojson", data: EMPTY });
          gl.addLayer({
            id: "shapes-fill",
            type: "fill",
            source: "shapes",
            filter: ["==", ["geometry-type"], "Polygon"],
            paint: { "fill-color": ["get", "color"], "fill-opacity": ["get", "fillOpacity"] },
          });
          gl.addLayer({
            id: "shapes-line",
            type: "line",
            source: "shapes",
            filter: ["==", ["get", "dash"], ""],
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": ["get", "stroke"], "line-width": ["get", "strokeWidth"] },
          });
          gl.addLayer({
            id: "points-circle",
            type: "circle",
            source: "points",
            paint: {
              "circle-color": ["coalesce", ["get", "color"], pointColor],
              "circle-radius": ["case", ["boolean", ["feature-state", "active"], false], 10, 7],
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });
          gl.addLayer({
            id: "draw-fill",
            type: "fill",
            source: "draw",
            filter: ["==", ["geometry-type"], "Polygon"],
            paint: { "fill-color": pointColor, "fill-opacity": 0.12 },
          });
          gl.addLayer({
            id: "draw-line",
            type: "line",
            source: "draw",
            filter: ["==", ["geometry-type"], "LineString"],
            paint: { "line-color": pointColor, "line-width": 2, "line-dasharray": [3, 2] },
          });
          gl.addLayer({
            id: "draw-points",
            type: "circle",
            source: "draw",
            filter: ["==", ["geometry-type"], "Point"],
            paint: {
              "circle-radius": 5,
              "circle-color": ["case", ["==", ["get", "first"], 1], pointColor, "#ffffff"],
              "circle-stroke-color": pointColor,
              "circle-stroke-width": 2,
            },
          });
          if (scaleBar) map?.addControl(new lib.ScaleControl({ unit: "metric", maxWidth: 120 }), "bottom-left");
          setReady(true);
        });

        const refreshPopupPosition = () => {
          const current = popupRef.current;
          if (!current) return;
          const screen = gl.project([current.lng, current.lat]);
          setPopupAt((previous) =>
            previous && Math.abs(previous.x - screen.x) < 0.5 && Math.abs(previous.y - screen.y) < 0.5
              ? previous
              : { x: screen.x, y: screen.y },
          );
        };
        gl.on("move", refreshPopupPosition);
        gl.on("moveend", () => {
          setZoomLevel(gl.getZoom());
          const bounds = gl.getBounds();
          latest.current.onViewportChange?.({
            zoom: gl.getZoom(),
            bounds: {
              west: bounds.getWest(),
              south: bounds.getSouth(),
              east: bounds.getEast(),
              north: bounds.getNorth(),
            },
          });
        });
        gl.on("dataloading", () => setTilesBusy(true));
        gl.on("idle", () => setTilesBusy(false));
        /* Plitkalar `fetch` bilan olinadi — API mijozining avtomatik yangilanishi (refresh)
           ularga tegmaydi. Kirish tokeni (15 daqiqa) tugagach plitkalar 401 qaytaradi:
           bitta yengil so'rov tokenni yangilaydi, so'ng plitkalar qayta olinadi. */
        let lastAuthRefresh = 0;
        gl.on("error", (event) => {
          const status = (event.error as { status?: number } | undefined)?.status;
          // 204/404 — bo'sh plitka, xato emas
          if (status === 404 || status === 204) return;
          if (status === 401 && Date.now() - lastAuthRefresh > 15_000) {
            lastAuthRefresh = Date.now();
            authApi
              .me()
              .then(() => {
                /* `setTiles` plitka keshini tozalab, manzillarni qayta so'raydi. */
                latest.current.vectorLayers.forEach((layer) => {
                  (gl.getSource(`vt:${layer.id}`) as VectorTileSource | undefined)?.setTiles([tileUrl(layer)]);
                });
              })
              .catch(() => {
                /* sessiya tugagan — `sf:unauthorized` kirish sahifasiga qaytaradi */
              });
            return;
          }
          console.warn("Xarita:", event.error?.message ?? event);
        });
      })
      .catch((error: unknown) => {
        console.warn("Xarita kutubxonasi yuklanmadi:", error);
        if (!disposed) setUnsupported(true);
      });

    return () => {
      disposed = true;
      mapRef.current = null;
      vectorState.current.clear();
      dashGroups.current.clear();
      map?.remove();
    };
    // Xarita bir marta quriladi: keyingi o'zgarishlar quyidagi effektlarda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----------------------------------------------------------- podkladka */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !basemap?.url_template) return;
    const existing = map.getSource("base") as RasterTileSource | undefined;
    if (existing) {
      existing.setTiles([basemap.url_template]);
      return;
    }
    map.addSource("base", {
      type: "raster",
      tiles: [basemap.url_template],
      tileSize: 256,
      maxzoom: 19,
    });
    map.addLayer({ id: "base", type: "raster", source: "base" }, "shapes-fill");
  }, [ready, basemap?.url_template]);

  /* ------------------------------------------------------ tayyor shakllar */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource("shapes") as GeoJSONSource).setData(shapesToGeoJson(shapes));
    /* Punktirli stillar har xil bo'lishi mumkin — har bir naqsh uchun alohida chiziq qatlami. */
    const wanted = new Set(shapes.map((shape) => dashKey(shape.dash)).filter(Boolean));
    dashGroups.current.forEach((key) => {
      if (wanted.has(key)) return;
      map.removeLayer(`shapes-line:${key}`);
      dashGroups.current.delete(key);
    });
    wanted.forEach((key) => {
      if (dashGroups.current.has(key)) return;
      map.addLayer(
        {
          id: `shapes-line:${key}`,
          type: "line",
          source: "shapes",
          filter: ["==", ["get", "dash"], key],
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": ["get", "stroke"],
            "line-width": ["get", "strokeWidth"],
            "line-dasharray": key.split(",").map(Number),
          },
        },
        "points-circle",
      );
      dashGroups.current.add(key);
    });
  }, [ready, shapes]);

  /* ------------------------------------------------------- tayyor nuqtalar */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource("points") as GeoJSONSource).setData(
      pointsToGeoJson(points, resolveColor("var(--brand)", "#16a34a")),
    );
  }, [ready, points]);

  /* ------------------------------------------------------ vektor qatlamlar */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const state = vectorState.current;
    const wanted = new Map(vectorLayers.map((layer) => [layer.id, layer]));

    const remove = (id: string) => {
      const entry = state.get(id);
      if (!entry) return;
      entry.layerIds.forEach((layerId) => map.getLayer(layerId) && map.removeLayer(layerId));
      if (map.getSource(`vt:${id}`)) map.removeSource(`vt:${id}`);
      state.delete(id);
    };
    [...state.keys()].forEach((id) => {
      if (!wanted.has(id)) remove(id);
    });

    wanted.forEach((layer, id) => {
      const signature = JSON.stringify([
        layer.layerId, layer.version, layer.geometry, layer.color, layer.minZoom, layer.maxZoom,
        layer.fillOpacity, layer.strokeWidth, layer.strokeColor, layer.dash, layer.densityUntil,
      ]);
      const current = state.get(id);
      if (current?.signature === signature) return;
      remove(id);
      map.addSource(`vt:${id}`, {
        type: "vector",
        tiles: [tileUrl(layer)],
        // Zichlik kataklari aniq obyektlardan pastroq masshtabda ham keladi — manba 0 dan boshlanadi.
        minzoom: layer.densityUntil ? 0 : Math.max(0, layer.minZoom ?? 0),
        maxzoom: TILE_SOURCE_MAX_ZOOM,
        promoteId: "id",
      });
      const specs = vectorLayerSpecs(layer);
      specs.forEach((spec) => map.addLayer(spec, "shapes-fill"));
      state.set(id, { signature, layerIds: specs.map((spec) => spec.id) });
    });

    /* Tartib: ro'yxatdagi birinchi qatlam eng pastda, ustiga keyingilari. */
    vectorLayers.forEach((layer) => {
      state.get(layer.id)?.layerIds.forEach((layerId) => map.moveLayer(layerId, "shapes-fill"));
    });
  }, [ready, vectorLayers]);

  /* -------------------------------------------------------- bosish va hover */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;

    const interactive = () => {
      const ids: string[] = [];
      if (map.getLayer("points-circle")) ids.push("points-circle");
      latest.current.vectorLayers
        .slice()
        .reverse()
        .forEach((layer) =>
          vectorState.current
            .get(layer.id)
            ?.layerIds.filter((layerId) => !layerId.includes(":density"))
            .forEach((layerId) => ids.push(layerId)),
        );
      return ids;
    };

    const setSelected = (selection: { source: string; id: string } | null) => {
      const previous = selectedFeature.current;
      if (previous) {
        map.setFeatureState(
          previous.source.startsWith("vt:")
            ? { source: previous.source, sourceLayer: "f", id: previous.id }
            : { source: previous.source, id: previous.id },
          { selected: false, active: false },
        );
      }
      selectedFeature.current = selection;
      if (selection) {
        map.setFeatureState(
          selection.source.startsWith("vt:")
            ? { source: selection.source, sourceLayer: "f", id: selection.id }
            : { source: selection.source, id: selection.id },
          selection.source.startsWith("vt:") ? { selected: true } : { active: true },
        );
      }
    };

    const closePopup = () => {
      infoRequest.current += 1;
      setSelected(null);
      setPopupContent(null);
      setPopupAt(null);
    };

    const onClick = (event: MapMouseEvent) => {
      const { drawMode: drawing, popup: popupEnabled, onMapClick: mapClick } = latest.current;
      if (drawing) {
        setDrawPoints((previous) => [...previous, { lat: event.lngLat.lat, lon: event.lngLat.lng }]);
        return;
      }
      const box: [[number, number], [number, number]] = [
        [event.point.x - 5, event.point.y - 5],
        [event.point.x + 5, event.point.y + 5],
      ];

      /* 1) tayyor nuqta */
      const pointHit = map.getLayer("points-circle")
        ? map.queryRenderedFeatures(box, { layers: ["points-circle"] })[0]
        : undefined;
      if (pointHit) {
        const id = String(pointHit.properties?.id ?? "");
        const point = latest.current.points.find((item) => item.id === id);
        if (point) {
          const screen = map.project([point.lon, point.lat]);
          closePopup();
          setSelected({ source: "points", id });
          if (popupEnabled) {
            setPopupContent({ kind: "point", point, lng: point.lon, lat: point.lat });
            setPopupAt({ x: screen.x, y: screen.y });
          }
          const size = map.getCanvas();
          latest.current.onSelect?.(id, {
            x: screen.x,
            y: screen.y,
            width: size.clientWidth,
            height: size.clientHeight,
          });
          return;
        }
      }

      /* 2) vektor qatlam obyekti (eng yuqori qatlam birinchi) */
      if (popupEnabled && !mapClick) {
        const vectorIds = interactive().filter((id) => id !== "points-circle");
        const hit = vectorIds.length ? map.queryRenderedFeatures(box, { layers: vectorIds })[0] : undefined;
        if (hit) {
          const layer = latest.current.vectorLayers.find((item) => hit.source === `vt:${item.id}`);
          const featureId = String(hit.properties?.id ?? "");
          if (layer && featureId) {
            closePopup();
            setSelected({ source: hit.source, id: featureId });
            const properties = (hit.properties ?? {}) as Record<string, unknown>;
            setPopupContent({
              kind: "vector",
              layer,
              featureId,
              title: featureTitle(properties, layer),
              properties: {},
              lng: event.lngLat.lng,
              lat: event.lngLat.lat,
            });
            setPopupAt({ x: event.point.x, y: event.point.y });
            /* To'liq atributlar plitkada emas — bosilganda yengil so'rov bilan olinadi. */
            const ticket = ++infoRequest.current;
            gisApi
              .featureInfo(featureId)
              .then((info) => {
                if (ticket !== infoRequest.current) return;
                setPopupContent((current) =>
                  current?.kind === "vector" && current.featureId === featureId
                    ? {
                        ...current,
                        title: info.name || current.title,
                        properties: {
                          ...info.properties,
                          ...(info.area_ha ? { area_ha: info.area_ha } : {}),
                          ...(info.length_m ? { length_m: info.length_m } : {}),
                        },
                      }
                    : current,
                );
              })
              .catch(() => {
                /* atributlar olinmasa ham sarlavha ko'rinib turadi */
              });
            return;
          }
        }
      }

      /* 3) tayyor poligon */
      if (popupEnabled && !mapClick) {
        const hit = map.queryRenderedFeatures(event.point, { layers: ["shapes-fill"] })[0];
        const shape = hit ? latest.current.shapes.find((item) => item.id === String(hit.properties?.id)) : null;
        if (shape) {
          closePopup();
          setPopupContent({ kind: "shape", shape, lng: event.lngLat.lng, lat: event.lngLat.lat });
          setPopupAt({ x: event.point.x, y: event.point.y });
          return;
        }
        closePopup();
      }
      if (mapClick) {
        closePopup();
        mapClick({ lat: event.lngLat.lat, lon: event.lngLat.lng });
      }
    };

    /* Ikki marta bosish: chizish rejimida yakunlaydi (oddiy rejimda — kattalashtiradi). */
    const onDoubleClick = (event: MapMouseEvent) => {
      if (!latest.current.drawMode) return;
      event.preventDefault();
      /* ikkala bosish ham nuqta qo'shgan — bittasi ortiqcha */
      const points = drawRef.current.slice(0, -1);
      if (points.length < 3) {
        setDrawPoints(points);
        return;
      }
      const ring: Array<[number, number]> = points.map((point) => [point.lon, point.lat]);
      ring.push(ring[0]);
      setDrawPoints([]);
      onDrawFinishRef.current?.(ring);
    };

    let hoverFrame = 0;
    const onMove = (event: MapMouseEvent) => {
      if (hoverFrame) return;
      hoverFrame = requestAnimationFrame(() => {
        hoverFrame = 0;
        if (latest.current.drawMode) return;
        const ids = interactive();
        const hit = ids.length
          ? map.queryRenderedFeatures(
              [[event.point.x - 3, event.point.y - 3], [event.point.x + 3, event.point.y + 3]],
              { layers: ids },
            ).length > 0
          : false;
        map.getCanvas().style.cursor = hit ? "pointer" : "";
      });
    };

    map.on("click", onClick);
    map.on("dblclick", onDoubleClick);
    map.on("mousemove", onMove);
    return () => {
      cancelAnimationFrame(hoverFrame);
      map.off("click", onClick);
      map.off("dblclick", onDoubleClick);
      map.off("mousemove", onMove);
    };
  }, [ready]);

  /* ---------------------------------------------------------------- chizish */
  useEffect(() => {
    setDrawPoints([]);
    const map = mapRef.current;
    if (!ready || !map) return;
    if (drawMode) {
      map.doubleClickZoom.disable();
      map.getCanvas().style.cursor = "crosshair";
    } else {
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = "";
    }
  }, [ready, drawMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const coordinates = drawPoints.map((point) => [point.lon, point.lat]);
    const features: GeoJSON.Feature[] = coordinates.map((coordinate, index) => ({
      type: "Feature",
      properties: { first: index === 0 ? 1 : 0 },
      geometry: { type: "Point", coordinates: coordinate },
    }));
    if (coordinates.length > 1) {
      features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates } });
    }
    if (coordinates.length > 2) {
      features.push({
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [[...coordinates, coordinates[0]]] },
      });
    }
    (map.getSource("draw") as GeoJSONSource).setData({ type: "FeatureCollection", features });
  }, [ready, drawPoints]);

  const finishDrawing = useCallback(() => {
    if (drawPoints.length < 3) return;
    const ring: Array<[number, number]> = drawPoints.map((point) => [point.lon, point.lat]);
    ring.push(ring[0]);
    onDrawFinish?.(ring);
    setDrawPoints([]);
  }, [drawPoints, onDrawFinish]);

  /* --------------------------------------------------------- tashqi fokus */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !focus) return;
    if (focus.bounds) {
      const [w, s, e, n] = focus.bounds;
      map.fitBounds([[w, s], [e, n]], { padding: 60, maxZoom: 16, duration: prefersReducedMotion() ? 0 : 900 });
      return;
    }
    const target = { center: [focus.lon, focus.lat] as [number, number], zoom: focus.zoom ?? map.getZoom() };
    if (prefersReducedMotion()) map.jumpTo(target);
    else map.flyTo({ ...target, duration: 900 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, focus?.key]);

  /* ---------------------------------------------------------------- belgilar */
  useEffect(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (!ready || !map || !lib) return;
    const markers: GlMarker[] = [...(pin ? [pin] : []), ...pins].map((item) =>
      new lib.Marker({ element: pinElement(item), anchor: "bottom" }).setLngLat([item.lon, item.lat]).addTo(map),
    );
    const dragHandler = latest.current.onPinDrag;
    if (pin && dragHandler) {
      const marker = markers[0];
      marker.setDraggable(true);
      marker.getElement().classList.add("is-draggable");
      marker.on("dragend", () => {
        const at = marker.getLngLat();
        latest.current.onPinDrag?.({ lat: at.lat, lon: at.lng });
      });
    }
    return () => markers.forEach((marker) => marker.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, pin, pins, Boolean(onPinDrag)]);

  /* --------------------------------------------------------------- yaqinlashish */
  const fitToData = useCallback(() => {
    const map = mapRef.current;
    if (!map || !dataBounds) return;
    const canvas = map.getCanvas();
    let left = FIT_PADDING + (fitInsets.left ?? 0);
    let right = FIT_PADDING + TOOLS_WIDTH + (fitInsets.right ?? 0);
    /* Tor ekranda panel xaritaning deyarli hammasini egallaydi — hisobga olinmaydi. */
    if (canvas.clientWidth - left - right < canvas.clientWidth * 0.4) {
      left = FIT_PADDING;
      right = FIT_PADDING + TOOLS_WIDTH;
    }
    let top = FIT_PADDING + (fitInsets.top ?? 0);
    let bottom = FIT_PADDING + (fitInsets.bottom ?? 0);
    if (canvas.clientHeight - top - bottom < canvas.clientHeight * 0.4) {
      top = FIT_PADDING;
      bottom = FIT_PADDING;
    }
    map.fitBounds(
      [[dataBounds.west, dataBounds.south], [dataBounds.east, dataBounds.north]],
      {
        padding: { top, bottom, left, right },
        maxZoom: FIT_MAX_ZOOM,
        duration: prefersReducedMotion() ? 0 : FLIGHT_MS,
      },
    );
  }, [dataBounds, fitInsets]);

  /* Ro'yxatdan tanlangan obyekt chegarasiga yaqinlashish. */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !fitTarget) return;
    const { bounds } = fitTarget;
    map.fitBounds([[bounds.west, bounds.south], [bounds.east, bounds.north]], {
      padding: { top: 48, bottom: 48, left: 48, right: 48 + TOOLS_WIDTH },
      maxZoom: FIT_MAX_ZOOM,
      duration: prefersReducedMotion() ? 0 : FLIGHT_MS,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, fitTarget?.key]);

  /* ------------------------------------------------------------------ render */
  const activePopup = popupContent;
  const hasData = points.length > 0 || shapes.length > 0 || vectorLayers.length > 0;
  const showCover = loading && !hasData;

  return (
    <div
      className={`map-canvas${drawMode ? " is-drawing" : ""}`}
      role="application"
      aria-label="Interaktiv xarita"
      aria-busy={tilesBusy || loading}
    >
      <div className="map-gl" ref={host} />

      {popup && activePopup && popupAt ? (
        <div className="map-popup" style={{ left: popupAt.x, top: popupAt.y }}>
          <button
            type="button"
            className="map-popup-close"
            aria-label="Yopish"
            onClick={() => {
              infoRequest.current += 1;
              const previous = selectedFeature.current;
              const map = mapRef.current;
              if (previous && map) {
                map.setFeatureState(
                  previous.source.startsWith("vt:")
                    ? { source: previous.source, sourceLayer: "f", id: previous.id }
                    : { source: previous.source, id: previous.id },
                  { selected: false, active: false },
                );
              }
              selectedFeature.current = null;
              setPopupContent(null);
              setPopupAt(null);
            }}
          >
            ×
          </button>
          {activePopup.kind === "point" ? (
            <>
              <b>{activePopup.point.title}</b>
              {activePopup.point.subtitle ? <span>{activePopup.point.subtitle}</span> : null}
              <PropertyList properties={activePopup.point.properties} />
            </>
          ) : null}
          {activePopup.kind === "shape" ? (
            <>
              <b>{activePopup.shape.title}</b>
              {activePopup.shape.subtitle ? <span>{activePopup.shape.subtitle}</span> : null}
              <PropertyList properties={activePopup.shape.properties} />
            </>
          ) : null}
          {activePopup.kind === "vector" ? (
            <>
              <b>{activePopup.title}</b>
              <span>{activePopup.layer.label}</span>
              <PropertyList properties={activePopup.properties} />
            </>
          ) : null}
        </div>
      ) : null}

      {drawMode ? (
        <div className="map-draw-hint" role="status">
          {drawPoints.length === 0
            ? "Hududni chizish uchun xaritada nuqtalarni bosing"
            : `${drawPoints.length} ta nuqta${drawPoints.length < 3 ? " — kamida 3 ta kerak" : " — tugatish uchun ikki marta bosing"}`}
        </div>
      ) : null}

      <div className="map-tools">
        {extraTools}
        {drawMode ? (
          <>
            <button
              type="button"
              onClick={finishDrawing}
              disabled={drawPoints.length < 3}
              aria-label="Chizishni tugatish"
              title="Chizishni tugatish"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" {...ICON} aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setDrawPoints([])}
              disabled={drawPoints.length === 0}
              aria-label="Chizishni bekor qilish"
              title="Chizishni bekor qilish"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" {...ICON} aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </>
        ) : null}
        {onBack ? (
          <button type="button" onClick={onBack} aria-label="Barcha xaritalar" title="Barcha xaritalar">
            <svg width="16" height="16" viewBox="0 0 24 24" {...ICON} aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        ) : null}
        {onToggleFullscreen ? (
          <button
            type="button"
            onClick={onToggleFullscreen}
            aria-label={fullscreenActive ? "To'liq ekrandan chiqish" : "To'liq ekran"}
            title={fullscreenActive ? "To'liq ekrandan chiqish" : "To'liq ekran"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" {...ICON} aria-hidden="true">
              <path
                d={
                  fullscreenActive
                    ? "M8 3v3a2 2 0 0 1-2 2H3M16 3v3a2 2 0 0 0 2 2h3M8 21v-3a2 2 0 0 0-2-2H3M16 21v-3a2 2 0 0 1 2-2h3"
                    : "M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"
                }
              />
            </svg>
          </button>
        ) : null}
        {dataBounds !== undefined ? (
          <button
            type="button"
            disabled={!dataBounds}
            onClick={fitToData}
            aria-label="Ma'lumot bor hududga yaqinlashish"
            title={dataBounds ? "Ma'lumot bor hududga yaqinlashish" : "Ko'rsatiladigan ma'lumot hali yo'q"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" {...ICON} aria-hidden="true">
              <circle cx="12" cy="12" r="7" />
              <circle cx="12" cy="12" r="2.5" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
          </button>
        ) : null}
        <button type="button" onClick={() => mapRef.current?.zoomIn()} aria-label="Kattalashtirish">
          +
        </button>
        <span className="map-zoom-level" aria-hidden="true">
          {Math.round(zoomLevel)}
        </span>
        <button type="button" onClick={() => mapRef.current?.zoomOut()} aria-label="Kichiklashtirish">
          −
        </button>
        {basemaps.length > 1 ? (
          <div className="map-basemap">
            <button
              type="button"
              aria-label="Podkladkani tanlash"
              aria-expanded={basemapOpen}
              title={basemap?.name}
              onClick={() => setBasemapOpen((open) => !open)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" {...ICON} strokeWidth={1.6} aria-hidden="true">
                <path d="m12 2 10 5-10 5L2 7l10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </button>
            {basemapOpen ? (
              <ul className="map-basemap-list">
                {basemaps.map((row) => (
                  <li key={row.code}>
                    <button
                      type="button"
                      aria-pressed={row.code === basemap?.code}
                      className={row.code === basemap?.code ? "is-active" : undefined}
                      onClick={() => {
                        setBasemapCode(row.code);
                        setBasemapOpen(false);
                      }}
                    >
                      {row.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {tilesBusy && !showCover ? <span className="map-busy" role="status" aria-label="Xarita yangilanmoqda" /> : null}
      </div>

      {showCover ? (
        <div className="map-loading" role="status" aria-live="polite">
          <div className="map-loading-box">
            <div className="map-loading-bar is-indeterminate">
              <span />
            </div>
            <span>Qatlam yuklanmoqda</span>
          </div>
        </div>
      ) : null}

      {unsupported ? (
        <div className="map-loading" role="alert">
          <div className="map-loading-box">
            <b>Xaritani ko'rsatib bo'lmadi</b>
            <span>Brauzeringiz WebGL'ni qo'llab-quvvatlamaydi yoki u o'chirilgan.</span>
          </div>
        </div>
      ) : null}

      {basemap?.attribution ? <div className="map-attribution">{basemap.attribution}</div> : null}
    </div>
  );
}
